/**
 * Utility functions for working with OpenAlex keywords API
 */

const OPENALEX_API_BASE = 'https://api.openalex.org';

/**
 * Fetches popular keywords from OpenAlex API
 * @param {number} limit - Number of keywords to fetch (max 200 per request)
 * @returns {Promise<Array>} Array of keyword objects
 */
export const getPopularKeywords = async (limit = 100) => {
  try {
    const response = await fetch(
      `${OPENALEX_API_BASE}/keywords?sort=works_count:desc&per-page=${Math.min(limit, 200)}`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error fetching popular keywords:', error);
    return [];
  }
};

/**
 * Searches for keywords based on user query
 * @param {string} query - Search query
 * @param {number} limit - Number of results to return
 * @returns {Promise<Array>} Array of matching keyword objects
 */
export const searchKeywords = async (query, limit = 20) => {
  if (!query || query.trim().length === 0) {
    return [];
  }

  try {
    const searchQuery = encodeURIComponent(query.trim().toLowerCase());
    const url = `${OPENALEX_API_BASE}/keywords?search=${searchQuery}&per-page=${Math.min(limit, 200)}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error searching keywords:', error);
    return [];
  }
};

/**
 * Formats keyword for display in the UI
 * @param {Object} keyword - Keyword object from OpenAlex API
 * @returns {Object} Formatted keyword object
 */
export const formatKeywordForDisplay = (keyword) => {
  return {
    id: keyword.id,
    displayName: keyword.display_name,
    worksCount: keyword.works_count || 0,
    citedByCount: keyword.cited_by_count || 0,
    // Extract the keyword slug from the ID for backend compatibility
    slug: keyword.id ? keyword.id.split('/').pop() : keyword.display_name.toLowerCase().replace(/\s+/g, '-')
  };
};

/**
 * Filters out already selected keywords from search results
 * @param {Array} searchResults - Array of keyword search results
 * @param {Array} selectedKeywords - Array of already selected keywords
 * @returns {Array} Filtered array of keywords
 */
export const filterSelectedKeywords = (searchResults, selectedKeywords) => {
  const selectedIds = new Set(selectedKeywords.map(k => k.id));
  return searchResults.filter(keyword => !selectedIds.has(keyword.id));
};

/**
 * Debounce function for search input
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Cache for storing frequently accessed keywords
 */
class KeywordsCache {
  constructor() {
    this.cache = new Map();
    this.maxSize = 500; // Maximum number of cached items
  }

  get(key) {
    if (this.cache.has(key)) {
      // Move to end to mark as recently used
      const value = this.cache.get(key);
      this.cache.delete(key);
      this.cache.set(key, value);
      return value;
    }
    return null;
  }

  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Remove least recently used item
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  clear() {
    this.cache.clear();
  }
}

// Global cache instance
export const keywordsCache = new KeywordsCache();

/**
 * Cached search function that uses the cache for frequently searched terms
 * @param {string} query - Search query
 * @param {number} limit - Number of results to return
 * @returns {Promise<Array>} Array of matching keyword objects
 */
export const cachedSearchKeywords = async (query, limit = 20) => {
  const cacheKey = `search:${query.toLowerCase()}:${limit}`;
  
  // Check cache first
  const cachedResult = keywordsCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  // If not in cache, fetch from API
  const results = await searchKeywords(query, limit);
  
  // Cache the results
  keywordsCache.set(cacheKey, results);
  
  return results;
};

/**
 * Cached function to get popular keywords
 * @param {number} limit - Number of keywords to fetch
 * @returns {Promise<Array>} Array of popular keyword objects
 */
export const cachedGetPopularKeywords = async (limit = 100) => {
  const cacheKey = `popular:${limit}`;
  
  // Check cache first
  const cachedResult = keywordsCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  // If not in cache, fetch from API
  const results = await getPopularKeywords(limit);
  
  // Cache the results for longer since popular keywords don't change often
  keywordsCache.set(cacheKey, results);
  
  return results;
};

/**
 * Formats works count for display (e.g., 1234 -> "1.2K", 1234567 -> "1.2M")
 * @param {number} count - Works count
 * @returns {string} Formatted count string
 */
export const formatWorksCount = (count) => {
  if (!count) return '0';
  
  if (count < 1000) {
    return count.toString();
  } else if (count < 1000000) {
    return (count / 1000).toFixed(1) + 'K';
  } else {
    return (count / 1000000).toFixed(1) + 'M';
  }
};